'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Onboarding } from './Onboarding';
import { createApiClient, type MeResponse } from '@/lib/api';
import { getInitData, isInTelegram } from '@/lib/tma';

/**
 * Глобальный gate согласия. Оборачивает все страницы Mini App'а в layout —
 * до тех пор, пока юзер не принял Terms+Privacy, поверх контента отображается
 * Onboarding (fixed inset-0 z-[60]) и любые попытки навигации к /market,
 * /profile и т.д. блокируются модалкой.
 *
 * Backend дополнительно защищает чувствительные endpoints через
 * agreementGate middleware — без accept'а bet/deposit/withdraw/leaderboard/
 * disputes возвращают 403 AGREEMENT_REQUIRED. Этот компонент — UX-слой,
 * чтобы юзер не видел disabled-кнопки и не получал 403 от каждого действия.
 *
 * Pathname-исключения:
 *   • /admin/*       — операторы не должны видеть онбординг (у них свой
 *                      adminAuthMiddleware и role-whitelist).
 *   • /terms, /privacy — должны быть доступны для прочтения ИЗ онбординга
 *                      (ссылки в чекбоксах открывают эти страницы).
 */

const SKIPPED_PREFIXES = ['/admin', '/terms', '/privacy'];

export function AgreementGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // undefined — загружаем; null — вне Telegram, fallback на localStorage;
  // object — статус с сервера. Onboarding сам интерпретирует все три.
  const [agreement, setAgreement] = useState<
    MeResponse['agreement'] | null | undefined
  >(isInTelegram() ? undefined : null);

  useEffect(() => {
    // Загружаем agreement только в Telegram (вне TG — fallback localStorage).
    if (!isInTelegram()) return;
    const api = createApiClient(getInitData);
    api
      .me()
      .then((r) => setAgreement(r.agreement))
      .catch(() => {
        // /api/me недоступен → Onboarding покажется через localStorage fallback.
        setAgreement(null);
      });
  }, []);

  const isSkipped = SKIPPED_PREFIXES.some((p) => pathname?.startsWith(p));

  function handleAccepted() {
    setAgreement((prev) =>
      prev && typeof prev === 'object' ? { ...prev, accepted: true } : prev,
    );
  }

  return (
    <>
      {!isSkipped && (
        <Onboarding agreement={agreement} onAccepted={handleAccepted} />
      )}
      {children}
    </>
  );
}
